import React from 'react';
import { Bot } from 'lucide-react';

const AdminDashboardHome = ({ agents, categories, subcategories, onViewChange }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to the KL Accelerators Admin Panel</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onViewChange('agents')}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <Bot className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{agents.length}</h3>
              <p className="text-sm text-gray-600">Accelerators</p>
            </div>
          </div>
          <div className="mt-4 text-xs text-blue-600 font-medium">Click to manage →</div>
        </div>

        <div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onViewChange('categories')}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <span className="text-lg">📁</span>
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{categories.length}</h3>
              <p className="text-sm text-gray-600">Categories</p>
            </div>
          </div>
          <div className="mt-4 text-xs text-green-600 font-medium">Click to manage →</div>
        </div>

        <div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onViewChange('subcategories')}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <span className="text-lg">📂</span>
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{subcategories.length}</h3>
              <p className="text-sm text-gray-600">Subcategories</p>
            </div>
          </div>
          <div className="mt-4 text-xs text-purple-600 font-medium">Click to manage →</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            onClick={() => onViewChange('agents')}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
          >
            <div className="text-2xl mb-2">🤖</div>
            <div className="font-medium">Manage Accelerators</div>
            <div className="text-sm text-gray-600">Add, edit, or delete Accelerators</div>
          </button>
          <button 
            onClick={() => onViewChange('categories')}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
          >
            <div className="text-2xl mb-2">📁</div>
            <div className="font-medium">Manage Categories</div>
            <div className="text-sm text-gray-600">Organize agent categories</div>
          </button>
          <button 
            onClick={() => onViewChange('config')}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
          >
            <div className="text-2xl mb-2">⚙️</div>
            <div className="font-medium">Site Settings</div>
            <div className="text-sm text-gray-600">Configure website content</div>
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left block transition-colors"
          >
            <div className="text-2xl mb-2">🌐</div>
            <div className="font-medium">View Website</div>
            <div className="text-sm text-gray-600">Preview live site</div>
          </a>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">System Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">Database Connection</span>
            </div>
            <span className="text-sm text-green-600 font-medium">Connected</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">API Services</span>
            </div>
            <span className="text-sm text-green-600 font-medium">Online</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-medium">Last Updated</span>
            </div>
            <span className="text-sm text-blue-600 font-medium">{new Date().toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm text-gray-700">System initialized with sample data</span>
            <span className="text-xs text-gray-500 ml-auto">Today</span>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">Admin panel accessed</span>
            <span className="text-xs text-gray-500 ml-auto">Now</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardHome;